/** @jsxImportSource @emotion/react */
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex } from "antd";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

type Props = {
  completed_sales: number;
  sales_product: number;
  sales_product_ratio: number;
  stock_product: number;
  stock_product_ratio: number;
  temp_product: number;
  total_product: number;
};

const ProductStatusCard = (props: Props) => {
  const theme = useTheme();

  const {
    completed_sales,
    sales_product,
    sales_product_ratio,
    stock_product,
    stock_product_ratio,
    temp_product,
    total_product,
  } = props;

  const data = [
    {
      name: "판매상품",
      value: sales_product,
      percent: sales_product_ratio,
      color: theme.color.green,
      border: "#0000001A",
    },
    {
      name: "재고상품",
      value: stock_product,
      percent: stock_product_ratio,
      color: theme.color.black,
      border: "#0000001A",
    },
    {
      name: "임시저장",
      value: temp_product,
      // percent: temp_product_ratio,
      color: theme.color.light_green,
      border: "#0000001A",
    },
  ];

  return (
    <Container>
      <ChartSection>
        <ResponsiveContainer width={150} height={150}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={20}
              outerRadius={75}
              stroke="none"
              isAnimationActive={false}
              startAngle={90}
              endAngle={-270}
              dataKey="value">
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </ChartSection>

      <TextSection>
        <div>
          <Title>총 상품 수</Title>
          <TotalCount>{total_product?.toLocaleString()}</TotalCount>
        </div>

        <Flex vertical gap="1rem">
          {data.map((item, idx) => (
            <InfoRow key={idx}>
              <Left>
                <ColorDot color={item.color} border={item.border} />
                <Label>{item.name}</Label>
                <Percent>
                  {item.name === "임시저장" ? "" : `${item.percent}%`}
                </Percent>
              </Left>
              <Right>{item.value.toLocaleString()}</Right>
            </InfoRow>
          ))}
        </Flex>

        <div>
          <InfoRow>
            <Left>
              <TagIcon>
                <Image
                  src={theme.image.tag_icon.default}
                  alt="tag_icon"
                  width="1.6rem"
                  height="1.6rem"
                />
              </TagIcon>
              <Label>판매완료</Label>
            </Left>
            <Right>{completed_sales?.toLocaleString()}</Right>
          </InfoRow>
        </div>
      </TextSection>
    </Container>
  );
};

export default ProductStatusCard;

const Container = styled.div(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "start",
  gap: "1.5rem",
  justifyContent: "space-between",
}));

const ChartSection = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
});

const TextSection = styled.div({
  display: "flex",
  minWidth: "18rem",
  flex: 1,
  flexDirection: "column",
  padding: "1rem",
  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
  borderRadius: "10px",
  backgroundColor: "#fff",
  gap: "1.5rem",
});

const Title = styled.div({
  color: "#666",
  fontSize: 14,
  marginBottom: 4,
});

const TotalCount = styled.div({
  fontSize: "2.4rem",
  fontWeight: 500,
  marginBottom: 6,
});

const InfoRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "14px",
  whiteSpace: "nowrap",
});

const Left = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 6,
});

const Right = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeight.bold,
  fontSize: "1.6rem",
}));

const ColorDot = styled.span(
  ({ color, border }: { color: string; border: string }) => ({
    width: "1rem",
    height: "1rem",
    borderRadius: "100%",
    backgroundColor: color,
    border: `1px solid ${border}`,
  }),
);

const Label = styled.div(({ theme }) => ({
  color: theme.color.gray,
}));

const Percent = styled.div(({ theme }) => ({
  color: theme.color.green,
}));

const TagIcon = styled.span({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
});
